// Lightweight loading placeholder for the Live In Club page. Rendered while
// the page query has no data yet — the route loader warms the cache WITHOUT
// blocking first paint (#85 perf), so the page can land before the list
// arrives. A short stack of glimmer blocks (the design system's `Skeleton`
// atom) that mirror the real filters + row rhythm, keeping the layout stable
// when the data swaps in. Purely decorative, so the whole tree is hidden from
// assistive tech.

import { useId } from 'react';

import { Skeleton } from '~/_shared/design-system/ui/atoms/skeleton/skeleton';

import './live-in-club-skeleton.css';

interface LiveInClubSkeletonProps {
	id?: string;
	/** How many placeholder rows to render. Matches a typical short roster. */
	rows?: number;
}

const LiveInClubSkeleton = ({ id, rows = 5 }: LiveInClubSkeletonProps) => {
	const generated_id = useId();
	// Stable string keys (not the map index) so the row list satisfies the
	// list-key lint without a real identity to key on.
	const rowKeys = Array.from(
		{ length: rows },
		(_, i) => `live-in-club-skeleton-${i}`,
	);
	return (
		<main
			id={id ?? `LiveInClubSkeleton-${generated_id}`}
			className='live-in-club-page'
			aria-hidden='true'
		>
			<div
				className='live-in-club-skeleton'
				data-testid='live-in-club-skeleton'
			>
				<div className='live-in-club-skeleton__filters'>
					<Skeleton className='live-in-club-skeleton__label' />
					<Skeleton className='live-in-club-skeleton__tabs' />
					<Skeleton className='live-in-club-skeleton__search' />
				</div>
				<ul className='live-in-club-skeleton__list'>
					{rowKeys.map((key) => (
						<li key={key} className='live-in-club-skeleton__row'>
							<Skeleton
								shape='circle'
								className='live-in-club-skeleton__avatar'
							/>
							<Skeleton className='live-in-club-skeleton__line' />
							<Skeleton className='live-in-club-skeleton__action' />
						</li>
					))}
				</ul>
			</div>
		</main>
	);
};

export type { LiveInClubSkeletonProps };
export { LiveInClubSkeleton };
